// Per-page renderer for "dashboard" — extracted from reportBuilder.js.
// Signature: async (api, doc, ctx) => { y, aiInsights, aiRecommendations }
import autoTable from 'jspdf-autotable';
import { NAVY, formatValue, fetchPageAI } from '../utils';
import { drawVerticalBarChart, drawLineChart, drawDonutChart } from '../charts';

export async function renderDashboard(api, doc, ctx) {
  const { qs, filterDesc, cs, pw, page, filters } = ctx;
  let y = ctx.y;
  let aiInsights = [];
  let aiRecommendations = [];

  const [statsRes, trendsRes, savingsRes, p2pRes, payTermsRes] = await Promise.all([
    api.get(`/dashboard/stats?${qs}`),
    api.get(`/analytics/trends?${qs}`),
    api.get('/ai/savings-summary').catch(() => ({ data: {} })),
    api.get(`/analytics/p2p-summary${qs ? `?${qs}` : ''}`).catch(() => ({ data: {} })),
    api.get(`/analytics/payment-terms${qs ? `?${qs}` : ''}`).catch(() => ({ data: {} })),
  ]);
  const stats = statsRes.data;
  const trends = trendsRes.data;
  const savings = savingsRes.data;
  const p2pSummary = p2pRes.data || {};
  const paymentTerms = payTermsRes.data || {};

  // Build data context for AI
  const catSpend = stats.category_spend || {};
  const catLines = Object.entries(catSpend).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([name, value]) => `- ${name}: ${cs}${value?.toLocaleString()}`).join('\n');
  const topSupLines = (stats.top_suppliers || []).map(s => `- ${s.name}: ${cs}${s.total_spend?.toLocaleString()} (${s.category})`).join('\n');
  const trendLine = trends?.length > 0 ? `Monthly trend: ${trends.map(t => `${t.month}: ${cs}${t.spend?.toLocaleString()}`).join(', ')}` : '';
  const savingsLine = savings.has_insights ? `Potential Savings: ${cs}${savings.total_savings_low?.toLocaleString()} - ${cs}${savings.total_savings_high?.toLocaleString()}` : '';

  const dataContext = `Total Spend: ${cs}${stats.total_spend?.toLocaleString()}\nSuppliers: ${stats.supplier_count}\nTransactions: ${stats.transaction_count}\nMaverick composite (% of spend): ${stats.maverick_percentage?.toFixed(1)}%\n${savingsLine}\n${p2pSummary.has_data ? `PO Coverage (% of transactions): ${p2pSummary.po_coverage_pct}% | 2-Way Match (% of transactions): ${p2pSummary.two_way_match_rate}% | 3-Way Match (% of transactions): ${p2pSummary.three_way_match_rate}% | Contracted (% of spend): ${p2pSummary.contracted_pct}% | Non-PO (% of spend): ${p2pSummary.non_po_spend_pct}%` : ''}\n\nCategory Breakdown:\n${catLines}\n\nTop Suppliers:\n${topSupLines}\n\n${trendLine}`;

  try {
    const aiRes = await fetchPageAI(api, { page, dataContext, filterDesc, filters });
    aiInsights = aiRes.insights;
    aiRecommendations = aiRes.recommendations;
  } catch {}

  // KPI cards
  const savingsLabel = savings.has_insights ? formatValue(savings.total_savings_mid || 0) : 'N/A';
  autoTable(doc, {
    startY: y,
    head: [['Total Spend', 'Potential Savings', 'Suppliers', 'Transactions', 'Maverick (% of spend)']],
    body: [[`${cs}${stats.total_spend?.toLocaleString()}`, savingsLabel, String(stats.supplier_count), String(stats.transaction_count), `${stats.maverick_percentage?.toFixed(1)}%`]],
    styles: { fontSize: 9, halign: 'center' },
    headStyles: { fillColor: NAVY },
    columnStyles: { 1: { textColor: [16, 124, 65], fontStyle: 'bold' } },
    margin: { left: 14, right: 14 },
  });
  y = doc.lastAutoTable.finalY + 8;

  if (trends?.length > 0) {
    const chartData = trends.map(t => ({ label: t.month, value: t.spend }));
    y = drawLineChart(doc, 14, y, pw - 28, 65, chartData, { title: 'Spend Trends' });
  }

  const catEntries = Object.entries(catSpend).sort((a, b) => b[1] - a[1]);
  if (catEntries.length > 0) {
    if (y > 190) { doc.addPage(); y = 15; }
    const topCats = catEntries.slice(0, 8);
    const otherSpend = catEntries.slice(8).reduce((sum, [, v]) => sum + v, 0);
    const catData = topCats.map(([name, value]) => ({ label: name, value }));
    if (otherSpend > 0) catData.push({ label: 'Other', value: otherSpend });
    drawDonutChart(doc, 50, y + 30, 24, catData, { title: 'Spend by Category' });
    y += 75;
  }

  if (stats.top_suppliers?.length > 0) {
    if (y > 180) { doc.addPage(); y = 15; }
    const supData = stats.top_suppliers.map(s => ({ label: s.name, value: s.total_spend }));
    y = drawVerticalBarChart(doc, 14, y, pw - 28, 65, supData, { title: 'Top 5 Suppliers', maxBars: 5 });
    
    autoTable(doc, {
      startY: y,
      head: [['Top Supplier', 'Category', 'Total Spend']],
      body: stats.top_suppliers.map(s => [s.name, s.category || 'N/A', `${cs}${s.total_spend?.toLocaleString()}`]),
      styles: { fontSize: 7 }, headStyles: { fillColor: NAVY },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // P2P Summary panel — mirrors the 5-card P2P block shown on the Dashboard UI
  if (p2pSummary && p2pSummary.has_data) {
    if (y > 200) { doc.addPage(); y = 15; }
    autoTable(doc, {
      startY: y,
      head: [['Unique POs (% of txns)', '2-Way Match (% of txns)', '3-Way Match (% of txns)', 'Contracted (% of spend)', 'Non-PO Spend (% of spend)']],
      body: [[
        `${(p2pSummary.unique_pos || 0).toLocaleString()} (${p2pSummary.po_coverage_pct || 0}%)`,
        `${p2pSummary.two_way_match_rate || 0}%`,
        `${p2pSummary.three_way_match_rate || 0}%`,
        `${p2pSummary.contracted_pct || 0}%`,
        `${cs}${((p2pSummary.non_po_spend || 0) / 1e6).toFixed(1)}m (${Number(p2pSummary.non_po_spend_pct ?? 0).toFixed(1)}%)`,
      ]],
      styles: { fontSize: 8, halign: 'center' },
      headStyles: { fillColor: NAVY },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Payment Terms distribution — mirrors the paymentTerms chart on the UI
  if (paymentTerms?.distribution?.length > 0) {
    if (y > 200) { doc.addPage(); y = 15; }
    autoTable(doc, {
      startY: y,
      head: [['Payment Term', 'Spend', '% of Total', 'Transactions']],
      body: paymentTerms.distribution.slice(0, 10).map(p => [
        p.name,
        `${cs}${(p.value || 0).toLocaleString()}`,
        `${p.spend_percentage}%`,
        String(p.transaction_count || 0),
      ]),
      styles: { fontSize: 7 },
      headStyles: { fillColor: NAVY },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  return { y, aiInsights, aiRecommendations };
}
